import { FormControl, FormLabel, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import {
    useGetAllBuyerCompanies,
    useGetAllBuyerCompanyOptions,
    useGetWorkflowFieldDictionaryTemplate,
} from '../../hooks';
import { BusinessDocType, FieldDictionaryRecordType, RecursivePartial } from '../../types';
import { Autocomplete, Button, Checkbox, Form, FormButtons } from '../../ui';
import {
    FieldDictionaryEditorToolConfiguration,
    fieldDictionaryEditorToolConfigurationSchema,
} from './FieldDictionaryEditorToolConfiguration';

function RecordTypeToggleButtons({
    businessDocType,
    value,
    onChange,
}: {
    value: FieldDictionaryRecordType;
    onChange: (recordType: FieldDictionaryRecordType) => void;
    businessDocType: BusinessDocType;
}) {
    const handleChange = (event: React.MouseEvent<HTMLElement>, nextRecordType: FieldDictionaryRecordType | null) => {
        if (!nextRecordType) {
            return;
        }

        onChange(nextRecordType);
    };

    return (
        <FormControl>
            <FormLabel required>Record Type</FormLabel>
            <ToggleButtonGroup orientation="vertical" value={value} exclusive onChange={handleChange} sx={{ mt: 1 }}>
                <ToggleButton value={FieldDictionaryRecordType.BuyerLevelOverride} aria-label="Buyer-level Override">
                    Buyer-level Override
                </ToggleButton>
                {businessDocType === BusinessDocType.Invoice && (
                    <ToggleButton
                        value={FieldDictionaryRecordType.WorkflowLevelOverride}
                        aria-label="Workflow-level Override"
                    >
                        Workflow-level Override
                    </ToggleButton>
                )}
            </ToggleButtonGroup>
        </FormControl>
    );
}

function BusinessDocTypeToggleButtons({
    value,
    onChange,
}: {
    value: BusinessDocType;
    onChange: (businessDocType: BusinessDocType) => void;
}) {
    const handleChange = (event: React.MouseEvent<HTMLElement>, nextBusinessDocType: BusinessDocType | null) => {
        if (!nextBusinessDocType) {
            return;
        }

        onChange(nextBusinessDocType);
    };

    return (
        <FormControl>
            <FormLabel required>Business Doc Type</FormLabel>
            <ToggleButtonGroup orientation="vertical" value={value} exclusive onChange={handleChange} sx={{ mt: 1 }}>
                <ToggleButton value={BusinessDocType.Invoice} aria-label="Invoice">
                    Invoice
                </ToggleButton>
                <ToggleButton value={BusinessDocType.PurchaseOrder} aria-label="Purchase Order">
                    Purchase Order
                </ToggleButton>
                <ToggleButton
                    value={BusinessDocType.PurchaseOrderAcknowledgement}
                    aria-label="Purchase Order Acknowledgement"
                >
                    Purchase Order Acknowledgement
                </ToggleButton>
                <ToggleButton value={BusinessDocType.ASNGeneric} aria-label="ASN">
                    ASN
                </ToggleButton>
            </ToggleButtonGroup>
        </FormControl>
    );
}

function BuyerCompanyAutocomplete({ value, onChange }: { value: number | null; onChange: (value: number) => void }) {
    const { data: buyerCompanies, isLoading } = useGetAllBuyerCompanies();

    const buyerCompanyOptions = useGetAllBuyerCompanyOptions({ buyerCompanies: buyerCompanies ?? [] });

    const selectedOption = useMemo(() => {
        if (!value) {
            return null;
        }

        return buyerCompanyOptions.find((option) => option.value === value.toString()) ?? null;
    }, [value, buyerCompanyOptions]);

    return (
        <Autocomplete
            id="buyer-company-autocomplete"
            label="Buyer Company"
            loading={isLoading}
            value={selectedOption}
            disableClearable
            required
            onChange={(option) => {
                if (!option) {
                    return;
                }

                onChange(Number(option.value));
            }}
            options={buyerCompanyOptions}
        />
    );
}

function WorkflowFieldDictionaryTemplateAutocomplete({
    value,
    onChange,
}: {
    value: number | undefined;
    onChange: (value: number | undefined) => void;
}) {
    const { data: workflowFieldDictionaryTemplates, isLoading } = useGetWorkflowFieldDictionaryTemplate();

    const templateOptions = useMemo(() => {
        if (!workflowFieldDictionaryTemplates) {
            return [];
        }

        return workflowFieldDictionaryTemplates.map((template) => ({
            label: template.Name,
            value: template.Id,
        }));
    }, [workflowFieldDictionaryTemplates]);

    const selectedOption = useMemo(() => {
        if (!value) {
            return null;
        }

        return templateOptions.find((option) => option.value === value) ?? null;
    }, [value, templateOptions]);

    return (
        <Autocomplete
            id="workflow-field-dictionary-template-autocomplete"
            label="Workflow Field Dictionary Template"
            loading={isLoading}
            value={selectedOption}
            options={templateOptions}
            onChange={(option) => {
                onChange(option?.value);
            }}
        />
    );
}

const defaultFormState: RecursivePartial<FieldDictionaryEditorToolConfiguration> = {
    businessDocType: BusinessDocType.Invoice,
    configuration: {
        recordType: FieldDictionaryRecordType.BuyerLevelOverride,
    },
    isPrintPreview: false,
};

export function FieldDictionaryEditorToolConfigurator({
    onConfigurationCompletion,
    onCancel,
}: {
    onCancel: () => void;
    onConfigurationCompletion: (
        configuration: FieldDictionaryEditorToolConfiguration,
        editorMode: 'create' | 'update'
    ) => void;
}) {
    const [formState, setFormState] = useState<Record<string, any>>(defaultFormState);

    const [editorMode, setEditorMode] = useState<'create' | 'update'>('update');

    const [errors, setErrors] = useState<z.ZodIssue[]>([]);

    const handleSubmit = () => {
        const result = fieldDictionaryEditorToolConfigurationSchema.safeParse(formState);

        if (!result.success) {
            setErrors(result.error.errors);
            return;
        }

        onConfigurationCompletion(result.data, editorMode);
    };
    return (
        <Form
            testId="field-dictionary-editor-tool-configurator"
            errorMessage={errors.map((error) => error.message).join('\n\n')}
            onSubmit={handleSubmit}
            columns={[
                <Stack flexGrow={1} spacing={2}>
                    <BusinessDocTypeToggleButtons
                        value={formState.businessDocType!}
                        onChange={(value) => {
                            // Workflow level override is only available for Invoice
                            if (
                                value !== BusinessDocType.Invoice &&
                                formState.configuration?.recordType === FieldDictionaryRecordType.WorkflowLevelOverride
                            ) {
                                setFormState((prev) => ({
                                    ...prev,
                                    configuration: { recordType: FieldDictionaryRecordType.BuyerLevelOverride },
                                }));
                            }

                            setFormState((prev) => ({ ...prev, businessDocType: value }));
                        }}
                    />
                    <RecordTypeToggleButtons
                        businessDocType={formState.businessDocType}
                        value={formState.configuration?.recordType!}
                        onChange={(value) => {
                            if (value !== FieldDictionaryRecordType.WorkflowLevelOverride) {
                                setFormState((prev) => ({
                                    ...prev,
                                    configuration: {
                                        ...prev.configuration,
                                        recordType: value,
                                        workflowFieldDictionaryTemplateId: undefined,
                                    },
                                }));

                                setEditorMode('update');
                                return;
                            }

                            setFormState((prev) => ({
                                ...prev,
                                isPrintPreview: false,
                                configuration: { ...prev.configuration, recordType: value },
                            }));

                            setEditorMode('create');
                        }}
                    />
                </Stack>,
                <Stack flexGrow={1} spacing={2}>
                    <BuyerCompanyAutocomplete
                        value={formState.targetBuyerCompanyId ?? null}
                        onChange={(value) => {
                            setFormState((prev) => ({ ...prev, targetBuyerCompanyId: value }));
                        }}
                    />
                    {formState.configuration?.recordType === FieldDictionaryRecordType.WorkflowLevelOverride && (
                        <>
                            <WorkflowFieldDictionaryTemplateAutocomplete
                                value={formState.configuration?.workflowFieldDictionaryTemplateId}
                                onChange={(value) => {
                                    if (!value) {
                                        setEditorMode('create');
                                    }

                                    setFormState((prev) => ({
                                        ...prev,
                                        configuration: {
                                            ...prev.configuration,
                                            workflowFieldDictionaryTemplateId: value,
                                        },
                                    }));
                                }}
                            />
                            <Stack direction="row" justifyContent="flex-end">
                                <ToggleButtonGroup
                                    color="primary"
                                    value={editorMode}
                                    exclusive
                                    size="small"
                                    onChange={(_, value) => {
                                        if (!value) {
                                            return;
                                        }
                                        setEditorMode(value);
                                    }}
                                    disabled={!formState.configuration?.workflowFieldDictionaryTemplateId}
                                    aria-label="Editor Mode"
                                >
                                    <ToggleButton value="create">
                                        {!!formState.configuration?.workflowFieldDictionaryTemplateId ? (
                                            <ContentCopyIcon />
                                        ) : (
                                            <AddOutlinedIcon />
                                        )}
                                        <Typography variant="button" pl={1}>
                                            {!!formState.configuration?.workflowFieldDictionaryTemplateId
                                                ? 'Copy'
                                                : 'Create'}
                                        </Typography>
                                    </ToggleButton>
                                    <ToggleButton value="update">
                                        <EditOutlinedIcon />
                                        <Typography variant="button" pl={1}>
                                            Update
                                        </Typography>
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Stack>
                        </>
                    )}
                    {formState.configuration?.recordType === FieldDictionaryRecordType.BuyerLevelOverride && (
                        <Checkbox
                            checked={formState.isPrintPreview}
                            onChange={(e) => setFormState((prev) => ({ ...prev, isPrintPreview: e.target.checked }))}
                            label="Print Preview"
                        />
                    )}
                </Stack>,
            ]}
            formButtons={
                <FormButtons
                    rightButtons={
                        <>
                            <Button variant="secondary" onClick={onCancel}>
                                Cancel
                            </Button>
                            <Button type="submit">Continue</Button>
                        </>
                    }
                />
            }
        />
    );
}
